// ============================================================================
// devCache.js — a gyorsítótár törlése, DE csak akkor, ha a program verziója
// ténylegesen változott a legutóbbi látogatás óta - a tesztváltozaton.
//
// Miért kell? A tesztelő minden KIADÁS után azt akarja látni, ami éppen
// telepítve van - ne egy korábbi build maradványait. Ehhez nem kell minden
// egyes megnyitáskor törölni: elég, ha a program verziójának változásakor
// történik meg, egyszer. Az éles változaton ez egyáltalán NEM fut: ott a
// gyorsítótár a felhasználó javára (offline app-héj) működik, és a saját
// verziózott CACHE_NAME-je amúgy is gondoskodik a frissítésről.
//
// A "verzió" a public/version.json tartalma (a build írja, lásd
// build-version.mjs). Az utoljára LÁTOTT verziót a localStorage őrzi -
// eszközönként/böngészőnként, a bejelentkezéstől függetlenül. A lekérdezés
// maga az updater.js fetchServerVersion()-je: nem tartunk fenn két,
// egymástól független forrást ugyanarra a kérdésre.
//
// Két réteg védekezik együtt, mert egyik sem elég önmagában:
//   1. ez a modul verzióváltáskor kiüríti a Cache Storage-ot,
//   2. a service worker dev környezetben eleve nem gyorsítótáraz, és a
//      böngésző HTTP-cache-ét is megkerüli (lásd service-worker.js).
// A 2. nélkül a service worker az 1. után újra feltölthetné a tárat; az
// 1. nélkül egy korábbi (még gyorsítótárazó) build cache-e ottmaradna, amíg
// a program verziója újra nem változik.
//
// SZÁNDÉKOSAN NEM töröljük: a bejelentkezést (IndexedDB), a nyelvi
// beállításokat (localStorage - az itteni saját kulcsunk kivételével) és a
// push-feliratkozást - ezek nem gyorsítótárak, és törlésükkel a tesztelőnek
// minden verzióváltáskor újra be kellene lépnie.
// ============================================================================

import { IS_PROD } from "./env.js";
import { fetchServerVersion } from "./updater.js";
import { log } from "./log.js";

// Eszközönként/böngészőnként tárolt kulcs: melyik verziónál történt utoljára
// gyorsítótár-ürítés. Külön névtér a többi (bejelentkezés-független) helyi
// beállítástól.
const LAST_CLEARED_VERSION_KEY = "lineage0_dev_cache_version";

/** Az összes Cache Storage tár kiürítése. Sosem dob hibát. */
async function clearAllCaches(storage) {
  if (!storage) return 0;
  try {
    const keys = await storage.keys();
    const results = await Promise.all(keys.map((key) => storage.delete(key)));
    return results.filter(Boolean).length;
  } catch (err) {
    log.error("[dev] a gyorsítótár törlése nem sikerült:", err);
    return 0;
  }
}

/**
 * Gyorsítótár-ürítés, DE csak verzióváltáskor - a tesztváltozaton.
 *
 * Sosem dob hibát: sem a hálózati kérés, sem a törlés kudarca nem
 * akaszthatja meg az indulást. Ha a szerver verziója nem állapítható meg
 * (pl. offline indulás), NEM törlünk találgatásból - inkább kihagyjuk, és a
 * következő sikeres ellenőrzéskor pótoljuk.
 *
 * @param {object} [opts] csak a teszteknek
 * @param {boolean} [opts.isProd]
 * @param {CacheStorage} [opts.cacheStorage]
 * @param {Storage} [opts.localStorage]
 * @param {() => Promise<string|null>} [opts.fetchVersion]
 * @returns {Promise<{cleared: boolean, version: string|null, deletedCount: number}>}
 */
export async function clearCachesOnVersionChange(opts = {}) {
  const isProd = opts.isProd !== undefined ? opts.isProd : IS_PROD;
  if (isProd) return { cleared: false, version: null, deletedCount: 0 };

  const cacheStorage = opts.cacheStorage !== undefined
    ? opts.cacheStorage
    : (typeof caches !== "undefined" ? caches : null);
  const store = opts.localStorage !== undefined
    ? opts.localStorage
    : (typeof localStorage !== "undefined" ? localStorage : null);
  const fetchVersion = opts.fetchVersion || fetchServerVersion;

  const currentVersion = await fetchVersion();
  if (!currentVersion) {
    return { cleared: false, version: null, deletedCount: 0 };
  }

  let lastVersion = null;
  try {
    lastVersion = store ? store.getItem(LAST_CLEARED_VERSION_KEY) : null;
  } catch (err) {
    // Privát böngészés / letiltott tárolás - nem kritikus, egyszerűen úgy
    // kezeljük, mintha még sosem láttuk volna ezt a verziót.
  }

  if (lastVersion === currentVersion) {
    return { cleared: false, version: currentVersion, deletedCount: 0 };
  }

  const deletedCount = await clearAllCaches(cacheStorage);

  try {
    if (store) store.setItem(LAST_CLEARED_VERSION_KEY, currentVersion);
  } catch (err) {
    // Ha nem menthető el, a következő induláskor újra törlünk - ártalmatlan.
  }

  log.debug(
    `[dev] verzióváltás (${lastVersion || "-"} → ${currentVersion}) - gyorsítótár törölve: ${deletedCount} tár`
  );
  return { cleared: true, version: currentVersion, deletedCount };
}
